{
  "hero": {
    "title": "Data Policy",
    "description": "Retention principles by data category, work ownership, and how to request account or data deletion. This page describes only the principles the platform actually follows today; it does not commit to retention periods that have not been formally approved."
  },
  "quickNav": {
    "label": "On this page",
    "retention": "Retention principles",
    "ownership": "Work ownership",
    "deletion": "Account & data deletion",
    "related": "Related pages"
  },
  "retention": {
    "heading": "Retention principles",
    "description": "The principles below apply to each data category. Most retention periods have not yet been formally approved by the operating institutions, so this page does not list unapproved figures for those categories; where a period has been approved (see below), it is stated explicitly.",
    "categories": {
      "account": {
        "title": "Account data",
        "description": "Kept for as long as the account is active. Users may request deletion or anonymization of account-related personal data, though some competition history may still need to be retained for the audit reasons described below."
      },
      "submission": {
        "title": "Competition submissions & registrations",
        "description": "Finalized submissions and registration records are retained for as long as they remain relevant to that competition (post-event showcase, dispute handling, audit needs). Whether to remove them is a decision made by that competition's organizer, per its own rules."
      },
      "eligibilityProof": {
        "title": "Eligibility proof files",
        "description": "Raw enrollment/eligibility-proof documents you upload are automatically and permanently deleted 365 days after the competition they relate to ends. The verification outcome (approved/rejected, reviewer, timestamp) and audit trail are kept even after the file itself is deleted."
      },
      "audit": {
        "title": "Audit records",
        "description": "Audit records for scores, permissions, and other sensitive state changes are append-only (never edited or deleted) to support later review and dispute resolution. They are not automatically removed by a general deletion request."
      },
      "backup": {
        "title": "Backups",
        "description": "System backups exist for disaster recovery and reliability. Backup data rotates on the normal backup cycle and is not used as long-term archival storage."
      },
      "event": {
        "title": "Event service data",
        "description": "Event rider contact data (including the encrypted mobile number), meal and banquet replies, dietary notes, reminder consents and delivery records are deleted automatically 30 days after the event by default (an approved retention period); shuttle bookings, check-in records and audit records are kept for audit needs."
      }
    }
  },
  "ownership": {
    "heading": "Work ownership",
    "items": {
      "copyright": "Copyright in a submitted work generally remains with the participant (or follows the participant's institution's own rules regarding advisors/schools). The platform does not claim copyright over submissions.",
      "display": "Whether an organizer may publicly display a submission follows that competition's own published rules. Where a competition offers a post-event showcase, including an individual work in it is opt-in (requires the participant's consent), not a default.",
      "license": "By submitting a work, a participant grants the platform and the competition organizer the access needed to carry out judging, score calculation, and (where consented) showcase display. This does not transfer copyright."
    }
  },
  "deletion": {
    "heading": "Account & data deletion",
    "description": "Suspending an account is not the same as completing personal-data deletion: suspension only disables sign-in; the underlying data is not removed. You can request permanent account deletion from your Account & Security page while signed in; a Platform Admin reviews the request before it takes effect, and you can cancel it yourself any time before that review. Approval distinguishes:",
    "exportNote": "You can also download a JSON export of the personal data tied to your account (profile, submissions, scores you can see, and your recent account-related audit history) from the same Account & Security page, without needing to make a deletion request.",
    "categories": {
      "deletable": {
        "title": "Data that can be deleted outright",
        "description": "Login credentials (password/SSO link, active sessions) and any uploaded enrollment-proof files are deleted immediately on approval."
      },
      "anonymizable": {
        "title": "Data that must be anonymized rather than deleted",
        "description": "Your account profile (display name, email, avatar, organization display name) is anonymized rather than deleted, so historical submissions, scores, and results you're linked to stay intact for audit and dispute purposes; you will no longer be identifiable in them. The email recorded on judge invitations and email team invitations addressed to you is anonymized too, the optional reason given when declining a judge invitation is cleared, and judge invitations you had not answered are cancelled."
      },
      "retained": {
        "title": "Data that must be retained for audit or official competition records",
        "description": "For example, published results and their associated audit records are kept for historical integrity and dispute handling, and will not be removed or altered by an individual's deletion request. What an audit record captured at the time of the event (for example the address an invitation was sent to) is append-only and is not rewritten."
      }
    },
    "note": "Approval anonymizes and deletes the data described above immediately; it does not wait for a separately scheduled cleanup. If a request is rejected instead, the account is unaffected and a new request can be submitted later."
  },
  "related": {
    "heading": "Related pages",
    "privacy": "Privacy Notice (what we collect, why, who can access it)",
    "terms": "Terms of Use",
    "trust": "Trust Center",
    "contact": "For a question not covered by the self-service options above, email"
  }
}
